import { RECOGNIZE_PLATFORMS } from "@redotech/redo-recognize-common/audience-metrics";
import * as z from "zod";

/**
 * Recognize shopper — a flattened view of the reveal engine's RevealResponse.profile.
 */

export const PlatformAudience = z.object({
  platform: z.enum(RECOGNIZE_PLATFORMS),
  /** null = profile found but the count is not public. Never 0. */
  followers: z.number().nullable(),
});
export type PlatformAudience = z.infer<typeof PlatformAudience>;

/** Mirrors the reveal's MediaItem, so nothing is lost mapping between them. */
export const RecognizeMediaMention = z.object({
  kind: z.enum(["podcast", "youtube", "blog", "article", "other"]),
  title: z.string(),
  url: z.string(),
  /** ISO-8601 when the reveal could date it. */
  publishedAt: z.string().nullable(),
});
export type RecognizeMediaMention = z.infer<typeof RecognizeMediaMention>;

export const RecognizeShopper = z.object({
  /** Identifies the reveal for the detail fetch, unambiguously. */
  revealProfileId: z.string(),
  /** Redo customer id where known, for the avatar and the customer link. */
  customerId: z.string(),
  name: z.string(),
  /** Criterion keys this shopper matches — what the Categories filter selects on. */
  categoryKeys: z.array(z.string()),
  /** Profile.age — the reveal engine does not derive this yet. */
  age: z.number().nullable(),
  /** Profile.gender — inferred, low confidence. */
  gender: z.enum(["male", "female"]).nullable(),
  state: z.string().nullable(),
  /** PropertyData.homeMarketValue. */
  homeMarketValue: z.number().nullable(),
  isVip: z.boolean(),
  lastOrderAt: z.string().nullable(),
  /** Display number of the most recent order, e.g. "#1001". */
  lastOrderNumber: z.string().nullable(),
  audiences: z.array(PlatformAudience),
  /**
   * RevealResponse.confidenceBand — reveal confidence, not the
   * restriction_confidence DB enum whose value set it coincidentally matches.
   */
  //redontIgnore
  confidenceBand: z.enum(["high", "medium", "low"]),
});
export type RecognizeShopper = z.infer<typeof RecognizeShopper>;

/** The list row plus the narrative fields, fetched only when a row is opened. */
export const RecognizeShopperDetail = RecognizeShopper.extend({
  /** Career.currentTitle + currentCompany, condensed for a list row. */
  headline: z.string().nullable(),
  /** Career.synopsis — the paragraph that says who this person is. */
  synopsis: z.string().nullable(),
  badges: z.array(z.string()),
  city: z.string().nullable(),
  /** Why this shopper matches the merchant's configurable VIP standards. */
  vipReason: z.string().nullable(),
  orders: z.number(),
  media: z.array(RecognizeMediaMention),
  /** Provenance.sources — cited URLs / provider names behind this profile. */
  sources: z.array(z.string()),
});
export type RecognizeShopperDetail = z.infer<typeof RecognizeShopperDetail>;

export const getRecognizeShoppers = {
  name: "getRecognizeShoppers" as const,
  input: z.object({}),
  output: z.object({ shoppers: z.array(RecognizeShopper) }),
};

export const getRecognizeShopper = {
  name: "getRecognizeShopper" as const,
  input: z.object({ revealProfileId: z.string() }),
  output: z.object({ shopper: RecognizeShopperDetail.nullable() }),
};
